import React, { useContext, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from "moment";
import { ClipLoader } from "react-spinners";
import { RefreshCw } from "lucide-react";

import AdminLayout from "../../components/Admin/AdminLayout";
import AdminPageShell from "../../components/Admin/AdminPageShell";
import { adminTableStyles } from "../../components/Admin/adminTableStyles";
import { generatePDF, generateLaPosteCSV } from "../../components/Admin/helper";
import { api } from "../../api/api";
import AuthContext from "../../contexts/AuthCtx";
import { toast } from "../../utils/toast";
import { DELIVERY_STATUS } from "../../utils/letterSend";

const deliveryOptions = [
  { value: DELIVERY_STATUS.PENDING, label: "En attente" },
  { value: DELIVERY_STATUS.IN_PROGRESS, label: "En cours" },
  { value: DELIVERY_STATUS.RECEIVED, label: "Reçue" },
];

const columns = [
  {
    name: "Email",
    selector: (row) => row.email || "—",
    grow: 1.8,
    wrap: true,
  },
  { name: "Sexe", selector: (row) => row.title, grow: 0.5 },
  { name: "Prénom", selector: (row) => row.firstName, grow: 1.5 },
  { name: "Nom", selector: (row) => row.birthName, grow: 1.5 },
  {
    name: "Date de naissance",
    selector: (row) => moment(row.date_of_Birth).format("DD/MM/YYYY"),
    width: "140px",
  },
  { name: "Commune", selector: (row) => row.place_of_birth, grow: 1.2 },
  {
    name: "Département",
    selector: (row) => row.birth_department_Number,
    width: "120px",
  },
  { name: "Pays", selector: (row) => row.nationality, width: "100px" },
  {
    name: "Date de décès",
    selector: (row) => moment(row.deathDate).format("DD/MM/YYYY"),
    width: "130px",
  },
  { name: "Score", selector: (row) => row.deathScore, width: "80px" },
];

function ExpandableRow({
  data,
  letters,
  updatingId,
  onUpdateStatus,
}) {
  const userLetters = letters?.filter(
    (letter) => letter?.userId?._id === data._id
  );

  if (!userLetters?.length) {
    return (
      <div className="bg-pageBg/40 px-6 py-5 font-josefin text-sm text-textMuted">
        Aucune lettre trouvée pour cet utilisateur.
      </div>
    );
  }

  const rows = userLetters.flatMap((letter) =>
    (letter.recipients || []).map((recipient) => ({
      id: `${letter._id}-${recipient._id}`,
      letter,
      recipient,
      letterTitle: letter.title,
      recipientName: [recipient.firstName, recipient.lastName]
        .filter(Boolean)
        .join(" "),
      deliveryStatus: recipient.deliveryStatus || DELIVERY_STATUS.PENDING,
      inProgressAt: recipient.inProgressAt || recipient.sentAt,
    }))
  );

  return (
    <div className="bg-pageBg/40 px-4 py-5 sm:px-6">
      <div className="overflow-x-auto rounded-xl border border-greyLight/30 bg-white">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-greyLight/30 bg-dashboardSidebar">
              <th className="px-4 py-3 text-left font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
                Titre
              </th>
              <th className="px-4 py-3 text-left font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
                Destinataire
              </th>
              <th className="px-4 py-3 text-left font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
                Statut
              </th>
              <th className="px-4 py-3 text-left font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
                En cours depuis
              </th>
              <th className="px-4 py-3 text-left font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
                Contenu
              </th>
              <th className="px-4 py-3 text-left font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
                Export
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-greyLight/20 font-josefin text-sm text-secondaryText last:border-0"
              >
                <td className="px-4 py-3">{row.letterTitle}</td>
                <td className="px-4 py-3">{row.recipientName || "—"}</td>
                <td className="px-4 py-3">
                  <select
                    value={
                      row.deliveryStatus === DELIVERY_STATUS.SCHEDULED
                        ? DELIVERY_STATUS.PENDING
                        : row.deliveryStatus
                    }
                    disabled={updatingId === row.recipient._id}
                    onChange={(e) =>
                      onUpdateStatus(row.recipient._id, e.target.value)
                    }
                    className="rounded-lg border border-greyLight bg-white px-2 py-1.5 font-josefin text-sm text-secondaryText outline-none focus:border-brandPurple"
                  >
                    {deliveryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  {row.inProgressAt
                    ? moment(row.inProgressAt).format("DD/MM/YYYY HH:mm")
                    : "—"}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() =>
                      generatePDF(row.letter.title, row.letter.content)
                    }
                    className="text-brandPurple underline-offset-2 hover:underline"
                  >
                    PDF
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => generateLaPosteCSV([row.recipient])}
                    className="text-brandPurple underline-offset-2 hover:underline"
                  >
                    CSV
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Expedition() {
  const [data, setData] = useState([]);
  const [letters, setLetters] = useState([]);
  const [changed, setChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const AuthCtx = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [usersResponse, lettersResponse] = await Promise.all([
          api.get("admin/getDeathUsers", {
            headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
          }),
          api.get("admin/getLetters", {
            headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
          }),
        ]);

        setData(usersResponse.data.users);
        setLetters(lettersResponse.data.lettersWithDiedUsers);
      } catch (err) {
        toast.error("Impossible de charger les données.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [AuthCtx.userToken, changed]);

  const alteredData = data.map((obj) => ({
    _id: obj._id,
    email: obj.email,
    ...obj.user_info,
  }));

  const updateDataHandler = async () => {
    setIsUpdating(true);
    try {
      await api.put(
        "admin/updateDeathStatus",
        {},
        { headers: { Authorization: `Bearer ${AuthCtx.userToken}` } }
      );
      setChanged((prev) => !prev);
      toast.success("Base de données mise à jour.");
    } catch (err) {
      toast.error("Erreur lors de la mise à jour.");
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDeliveryStatus = async (recipientId, deliveryStatus) => {
    setUpdatingId(recipientId);
    try {
      await api.put(
        `admin/updateDeliveryStatus/${recipientId}`,
        { deliveryStatus },
        { headers: { Authorization: `Bearer ${AuthCtx.userToken}` } }
      );
      setChanged((prev) => !prev);
      toast.success("Statut mis à jour.");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Erreur lors de la mise à jour."
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <AdminLayout>
      <AdminPageShell
        title="Expédition (décès)"
        subtitle="Utilisateurs décédés dont les lettres programmées doivent être expédiées."
        isLoading={isLoading}
        action={
          isUpdating ? (
            <div className="flex items-center gap-2 rounded-xl bg-dashboardColumn px-4 py-2.5 font-josefin text-sm text-textMuted">
              <ClipLoader color="#84a59d" size={20} />
              Mise à jour…
            </div>
          ) : (
            <button
              type="button"
              onClick={updateDataHandler}
              className="flex items-center gap-2 rounded-lg bg-sageDark px-5 py-2.5 font-josefin text-sm font-medium text-white transition hover:bg-modalSage"
            >
              <RefreshCw size={16} />
              Mettre à jour
            </button>
          )
        }
      >
        <DataTable
          columns={columns}
          data={alteredData}
          noHeader
          highlightOnHover
          pagination
          expandableRows
          expandableRowsComponent={({ data: rowData }) => (
            <ExpandableRow
              data={rowData}
              letters={letters}
              updatingId={updatingId}
              onUpdateStatus={updateDeliveryStatus}
            />
          )}
          customStyles={adminTableStyles}
          noDataComponent={
            <div className="py-12 text-center font-josefin text-sm text-textMuted">
              Aucun utilisateur décédé enregistré.
            </div>
          }
        />
      </AdminPageShell>
    </AdminLayout>
  );
}

export default Expedition;
